import React from 'react'
import { Box, VStack, Text, Button, useColorModeValue, Icon } from '@chakra-ui/react'
import { FaExclamationTriangle } from 'react-icons/fa'
import { useAuthContext } from '../../contexts/AuthContext'
import { AuthPage } from './AuthPage'
import { LoadingSpinner } from '../LoadingSpinner'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireEmailVerification?: boolean
  onAuthSuccess?: () => void
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireEmailVerification = false,
  onAuthSuccess 
}) => {
  const { user, loading, isAuthenticated, isEmailVerified } = useAuthContext()

  // Show loading spinner while checking auth state
  if (loading) {
    return <LoadingSpinner />
  }

  // If not authenticated, show auth page
  if (!isAuthenticated) {
    return <AuthPage onSuccess={onAuthSuccess} />
  }

  // If email verification is required but not verified
  if (requireEmailVerification && !isEmailVerified) {
    const bgColor = useColorModeValue('#09090b', '#09090b')
    const cardBg = useColorModeValue('#191919', '#191919')
    const borderColor = useColorModeValue('gray.700', 'gray.700')
    const textColor = useColorModeValue('white', 'white')
    
    return (
      <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center" p={4}>
        <Box maxW="md" mx="auto" bg={cardBg} rounded="lg" shadow="md" p={6} textAlign="center" border="1px" borderColor={borderColor}>
          <VStack spacing={4} mb={4}>
            <Icon as={FaExclamationTriangle} w={12} h={12} color="yellow.400" />
            <Text fontSize="lg" fontWeight="semibold" color={textColor}>
              Email Verification Required
            </Text>
          </VStack>
          <Text color="gray.300" mb={4}>
            Please verify your email address before accessing this feature.
          </Text>
          <Text fontSize="sm" color="gray.400" mb={4}>
            Check your inbox for a verification email from us.
          </Text>
          <Button
            w="full"
            bg="#3182ce"
            color="white"
            _hover={{ bg: '#2c5aa0' }}
            onClick={() => window.location.reload()}
          >
            I've Verified My Email
          </Button>
        </Box>
      </Box>
    )
  }

  // User is authenticated and verified (if required), show children
  return <>{children}</>
} 